import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import AddRiskDialog from '@/pages/risk/AddRiskDialog'
import { TreatmentLabel } from '@/pages/risk/RiskBadges'
import { PickerDialog } from '@/pages/risk/RiskDetailSections'
import type { VendorRiskResponse } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { Label } from '@deepguide-ai/ui/label'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import {
  DETAIL_FIELD_LABEL_CLASS,
  DETAIL_FIELD_VALUE_CLASS,
} from './detail-primitives'

const RESPONSE_STRATEGIES = ['MITIGATE', 'ACCEPT', 'TRANSFER', 'AVOID'] as const

export function VendorRiskResponseReadOnly({
  value,
}: {
  value?: VendorRiskResponse
}) {
  if (!value?.strategy) return null
  return (
    <section aria-label='Risk response' className='space-y-3'>
      <div>
        <p className={DETAIL_FIELD_LABEL_CLASS}>Risk response</p>
        <p className={DETAIL_FIELD_VALUE_CLASS}>
          <TreatmentLabel
            treatment={value.strategy}
            className={DETAIL_FIELD_VALUE_CLASS}
          />
        </p>
      </div>
      <div>
        <p className={DETAIL_FIELD_LABEL_CLASS}>Response rationale</p>
        <p className={`${DETAIL_FIELD_VALUE_CLASS} whitespace-pre-wrap`}>
          {value.rationale || 'Not recorded'}
        </p>
      </div>
      {value.strategy === 'MITIGATE' && value.riskIds.length > 0 && (
        <div>
          <p className={DETAIL_FIELD_LABEL_CLASS}>Mitigation risks</p>
          <p className={DETAIL_FIELD_VALUE_CLASS}>{value.riskIds.join(', ')}</p>
        </div>
      )}
    </section>
  )
}

export function VendorRiskResponseFields({
  value,
  vendorId,
  onChange,
}: {
  value?: VendorRiskResponse
  vendorId: string
  onChange: (value: VendorRiskResponse) => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }
  const queryClient = useQueryClient()
  const riskIds = value?.riskIds ?? []
  const risks = useQuery(
    trpc.riskRegister.listRisksForPicker.queryOptions(undefined, {
      enabled: pickerOpen || riskIds.length > 0,
    })
  )
  const link = useMutation(trpc.riskRegister.linkVendor.mutationOptions())
  const update = (patch: Partial<VendorRiskResponse>) =>
    onChange({ riskIds: [], ...value, ...patch })
  const addRisk = (id: string) => {
    update({ riskIds: [...new Set([...riskIds, id])] })
    void queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.listRisksForPicker.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.list.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.list.queryKey(),
    })
  }

  return (
    <section aria-label='Risk response' className='space-y-4'>
      <div className='space-y-2'>
        <Label
          id='vendor-risk-response-label'
          className={DETAIL_FIELD_LABEL_CLASS}
        >
          Risk response
        </Label>
        <div
          role='group'
          aria-labelledby='vendor-risk-response-label'
          className='grid grid-cols-2 gap-2 sm:grid-cols-4'
        >
          {RESPONSE_STRATEGIES.map((strategy) => (
            <Button
              key={strategy}
              type='button'
              variant='outline'
              aria-pressed={value?.strategy === strategy}
              className={`h-12 w-full ${value?.strategy === strategy ? 'border-ring bg-selected text-status-blue-text hover:bg-selected hover:text-status-blue-text dark:border-ring dark:bg-selected dark:hover:bg-selected' : 'text-muted-foreground'}`}
              onClick={() => update({ strategy })}
            >
              <TreatmentLabel treatment={strategy} className='' />
            </Button>
          ))}
        </div>
      </div>
      {value?.strategy && (
        <>
          <div>
            <Label
              htmlFor='vendor-response-rationale'
              className={DETAIL_FIELD_LABEL_CLASS}
            >
              Response rationale
            </Label>
            <Textarea
              id='vendor-response-rationale'
              value={value.rationale ?? ''}
              maxLength={5000}
              onChange={(event) => update({ rationale: event.target.value })}
              placeholder={
                value.strategy === 'TRANSFER'
                  ? 'Describe insurance or contractual protection, including its limits.'
                  : value.strategy === 'ACCEPT'
                    ? 'Explain why the remaining risk is acceptable.'
                    : value.strategy === 'AVOID'
                      ? 'Explain why this vendor should not be used.'
                      : 'Describe the mitigation work tracked in the linked Risks.'
              }
            />
          </div>
          {value.strategy === 'AVOID' && (
            <p className='text-muted-foreground text-sm'>
              Reject onboarding, or use Offboard Vendor in the vendor actions
              menu to end an existing relationship. Avoid cannot be approved.
            </p>
          )}
          {value.strategy === 'MITIGATE' && (
            <div className='space-y-3'>
              <p className='text-muted-foreground text-sm'>
                Link or create a Risk. Set its owner, due date, and treatment
                plan in Risk. Planned work does not lower this assessment's
                rating.
              </p>
              {riskIds.map((id) => {
                const risk = risks.data?.find((risk) => risk.id === id)
                return (
                  <div
                    key={id}
                    className='flex items-center justify-between gap-3'
                  >
                    <Link
                      to='/workspaces/$workspaceId/risks/$riskId'
                      params={{ workspaceId, riskId: id }}
                      className='text-cobalt-strong text-sm'
                    >
                      {risk ? `${risk.displayId} ${risk.title}` : id}
                    </Link>
                    <Button
                      variant='ghost'
                      size='smIcon'
                      aria-label={`Remove mitigation risk ${risk?.displayId ?? id}`}
                      onClick={() =>
                        update({
                          riskIds: riskIds.filter((riskId) => riskId !== id),
                        })
                      }
                    >
                      <X className='size-3.5' />
                    </Button>
                  </div>
                )
              })}
              {risks.isError && (
                <p role='alert' className='text-destructive text-sm'>
                  Could not load Risks.{' '}
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => void risks.refetch()}
                  >
                    Retry
                  </Button>
                </p>
              )}
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setPickerOpen(true)}
                >
                  Link Risk
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setCreateOpen(true)}
                >
                  Create Risk
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      <PickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title='Link a mitigation Risk'
        description='Choose a Risk to track this vendor’s mitigation work.'
        items={risks.data ?? []}
        excludedIds={new Set(riskIds)}
        searchPlaceholder='Search risks…'
        emptyMessage={risks.isLoading ? 'Loading risks…' : 'No matching risks.'}
        itemSearchText={(risk) => `${risk.displayId} ${risk.title}`}
        renderRow={(risk) => (
          <span>
            {risk.displayId} {risk.title}
          </span>
        )}
        onSelect={async (risk) => {
          if (link.isPending) return
          try {
            await link.mutateAsync({ riskId: risk.id, vendorId })
            addRisk(risk.id)
            setPickerOpen(false)
          } catch (error) {
            toast.error(
              error instanceof Error ? error.message : 'Could not link Risk'
            )
          }
        }}
      />
      {createOpen && (
        <AddRiskDialog
          open
          vendorId={vendorId}
          onClose={() => setCreateOpen(false)}
          onCreated={(risk) => {
            if (risk) addRisk(risk.id)
          }}
        />
      )}
    </section>
  )
}
